// Pure Logik des Konto-Umschalters mit Sammelposteingang (#535, Mockup 1b g):
// Kontoreihenfolge, Firmenfarbe und Kürzel je Konto, gemerkte Wahl, das
// Absenderkonto im Compose und die Schwelle fürs Herunterziehen. Ohne DOM
// testbar (mail-account-scope.test.ts); mail.tsx und die Sheet-Komponenten
// lesen nur hier ab.

import { MAIL_FILTERS, type MailListFilter } from './mail-filter'

/** „Alle Posteingänge" — der Sammelposteingang. Sonst steht die Konto-ID. */
export const MAIL_SCOPE_ALL = 'all'
export type MailScope = string

/** localStorage-Schlüssel der letzten Wahl („all" oder Konto-ID). */
export const MAIL_SCOPE_STORAGE_KEY = 'ember.mail.scope'

/** Firmenfarben nur aus vorhandenen Tokens; `warning` bleibt dem Schloss. */
export const ACCOUNT_TONES = ['accent', 'info', 'success'] as const
export type AccountTone = (typeof ACCOUNT_TONES)[number]

/** Ab dieser Zugstrecke (px) löst Herunterziehen die Aktualisierung aus. */
export const MAIL_PULL_THRESHOLD_PX = 64

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

interface OrderedAccount {
  id: string
  createdAt: string
}

/** Stabile Reihenfolge wie der Server (createdAt, dann id): die Farbe hängt
 *  an der Position und darf nicht mit der Antwortreihenfolge wandern. */
export function sortMailAccounts<T extends OrderedAccount>(accounts: readonly T[]): T[] {
  return [...accounts].sort((a, b) => {
    const byTime = Date.parse(a.createdAt) - Date.parse(b.createdAt)
    if (byTime !== 0 && Number.isFinite(byTime)) return byTime
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
  })
}

/** Firmenfarbe eines Kontos: accent, info, success, dann zyklisch. */
export function accountTone(accounts: readonly OrderedAccount[], accountId: string): AccountTone {
  const index = sortMailAccounts(accounts).findIndex((a) => a.id === accountId)
  return ACCOUNT_TONES[Math.max(0, index) % ACCOUNT_TONES.length]
}

/** Zwei Buchstaben aus dem Label: zwei Wörter → je der erste Buchstabe
 *  („Muster GmbH" → „MG"), ein Wort → die ersten zwei („Privat" → „PR"). */
export function accountInitials(label: string): string {
  const words = label
    .split(/[^\p{L}\p{N}]+/u)
    .map((word) => word.trim())
    .filter(Boolean)
  if (words.length === 0) return '?'
  const letters = words.length >= 2 ? `${words[0][0]}${words[1][0]}` : words[0].slice(0, 2)
  return letters.toLocaleUpperCase('de-DE')
}

/** Standard ohne gemerkte Wahl: „Alle" ab zwei Konten, sonst das einzige. */
export function defaultMailScope(accounts: readonly { id: string }[]): MailScope {
  return accounts.length === 1 ? accounts[0].id : MAIL_SCOPE_ALL
}

/** Gemerkte Wahl lesen. Unbekannte oder gelöschte Konto-ID → „all";
 *  kaputter Wert, leerer oder gesperrter Speicher → Standard. */
export function readMailScope(
  storage: Pick<Storage, 'getItem'> | null,
  accounts: readonly { id: string }[],
): MailScope {
  let raw: string | null = null
  try {
    raw = storage?.getItem(MAIL_SCOPE_STORAGE_KEY) ?? null
  } catch {
    raw = null
  }
  if (raw === MAIL_SCOPE_ALL) return MAIL_SCOPE_ALL
  if (raw && UUID.test(raw)) {
    const id = raw.toLowerCase()
    return accounts.some((a) => a.id.toLowerCase() === id) ? id : MAIL_SCOPE_ALL
  }
  return defaultMailScope(accounts)
}

/** Wahl merken; ein voller oder gesperrter Speicher ist kein Fehler. */
export function storeMailScope(storage: Pick<Storage, 'setItem'> | null, scope: MailScope): void {
  try {
    storage?.setItem(MAIL_SCOPE_STORAGE_KEY, scope)
  } catch {
    // Merken ist Komfort; ohne Speicher gilt beim nächsten Mal der Standard.
  }
}

/** Speicher des Browsers, falls erreichbar (privates Fenster, blockiert). */
export function browserStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

export interface ComposeAccountChoice {
  /** Absenderkonto; null nur ohne jedes Konto. */
  accountId: string | null
  /** Aus der Quellmail übernommen („Automatisch: an dieses Konto ging die Mail"). */
  auto: boolean
  /** Quelle geschützt (Konto oder Ordner): das Von-Feld ist gesperrt. */
  locked: boolean
}

/** Absenderkonto im Compose. Antworten und Weiterleiten nehmen immer das
 *  Konto, in dessen Postfach die Quelle liegt (auch in der Einzelansicht);
 *  eine geschützte Quelle sperrt die Wahl. Neue Mail aus „Alle" nimmt das
 *  zuletzt gewählte Einzelkonto, sonst das erste der Sortierung. */
export function composeAccount(opts: {
  mode: 'new' | 'reply' | 'forward' | 'draft'
  scope: MailScope
  source?: { accountId: string }
  sourceProtected: boolean
  lastAccountId: string | null
  accounts: readonly (OrderedAccount & { disabledAt?: string | null })[]
}): ComposeAccountChoice {
  if ((opts.mode === 'reply' || opts.mode === 'forward') && opts.source)
    return { accountId: opts.source.accountId, auto: true, locked: opts.sourceProtected }
  if (opts.scope !== MAIL_SCOPE_ALL && opts.accounts.some((a) => a.id === opts.scope))
    return { accountId: opts.scope, auto: false, locked: false }
  const sorted = sortMailAccounts(opts.accounts)
  // A disabled account cannot send (server: MAIL_ACCOUNT_DISABLED), so it is no last choice.
  const last = sorted.find((a) => a.id === opts.lastAccountId && !a.disabledAt)
  const first = sorted.find((a) => !a.disabledAt) ?? sorted[0]
  return { accountId: (last ?? first)?.id ?? null, auto: false, locked: false }
}

/** Konto für jede Aktion an einer Karte oder Mail: immer das der Mail selbst,
 *  nie ein gerade aktives Konto (Sammelposteingang!). */
export function actionAccountId(message: { accountId: string }): string {
  return message.accountId
}

/** Herunterziehen zum Aktualisieren: nur ganz oben in der Liste
 *  (scrollTop 0), nur senkrecht nach unten; ab 64 px bereit. */
export function mailPullState(
  start: { x: number; y: number; scrollTop: number },
  now: { x: number; y: number },
): { active: boolean; ready: boolean } {
  const dy = now.y - start.y
  const dx = now.x - start.x
  const active = start.scrollTop <= 0 && dy > 0 && dy > Math.abs(dx)
  return { active, ready: active && dy >= MAIL_PULL_THRESHOLD_PX }
}

/** Hinweiszeile unter den Chips im Sammelposteingang (#778-Chips, #535). */
export function unifiedFilterHint(filter: MailListFilter): string | null {
  if (filter === 'all') return null
  const label = MAIL_FILTERS.find((f) => f.id === filter)?.label ?? filter
  return `Filter: ${label} · alle Posteingänge`
}
